import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TablePagination,
  Chip,
} from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import { toast } from "react-toastify";
import { StyledTableCell, StyledTableRow } from "../../utils/constantData";
import {
  AdminPageLayout,
  AdminSectionCard,
  AdminAction,
  TableRowActions,
  DeleteConfirmationDialog,
} from "../../components/admin";
import {
  deleteComment,
  getCommentsByVideo,
  getVideoById,
  updateComment,
} from ".";
import EditCommentDialog from "./EditCommentDialog";

const formatDateTime = (value) => {
  if (!value) return "N/A";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "N/A";
  return date.toLocaleString("en-IN", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

function getUserName(comment) {
  return comment?.user?.name || comment?.user?.mobile || "N/A";
}

function flattenComments(comments) {
  const rows = [];
  for (const comment of comments || []) {
    rows.push({ ...comment, isReply: false });
    if (Array.isArray(comment.replies)) {
      for (const reply of comment.replies) {
        rows.push({ ...reply, isReply: true });
      }
    }
  }
  return rows;
}

export default function CommentsListPage() {
  const { videoId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const [videoTitle, setVideoTitle] = useState(location.state?.videoTitle || "");
  const [videoMeta, setVideoMeta] = useState({
    language: location.state?.videoLanguage || "",
    category: location.state?.videoCategory || "",
  });
  const [loadingVideo, setLoadingVideo] = useState(!location.state?.videoTitle);

  const [comments, setComments] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const [editTarget, setEditTarget] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const displayRows = useMemo(() => flattenComments(comments), [comments]);
  const startIndex = page * rowsPerPage;

  useEffect(() => {
    let cancelled = false;

    const loadVideo = async () => {
      if (!videoId) return;
      if (location.state?.videoTitle) {
        setVideoTitle(location.state.videoTitle);
        setVideoMeta({
          language: location.state.videoLanguage || "",
          category: location.state.videoCategory || "",
        });
        setLoadingVideo(false);
        return;
      }

      setLoadingVideo(true);
      try {
        const res = await getVideoById(videoId);
        if (cancelled) return;
        const video = res.data?.data || res.data?.video || res.data || {};
        setVideoTitle(video.title || `Video #${videoId}`);
        setVideoMeta({
          language: video.language || "",
          category: video.category || "",
        });
      } catch (err) {
        if (cancelled) return;
        setVideoTitle(`Video #${videoId}`);
      } finally {
        if (!cancelled) setLoadingVideo(false);
      }
    };

    loadVideo();
    return () => {
      cancelled = true;
    };
  }, [videoId, location.state]);

  const fetchComments = useCallback(async () => {
    if (!videoId) {
      setComments([]);
      setTotal(0);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const res = await getCommentsByVideo(videoId, {
        page: page + 1,
        limit: rowsPerPage,
      });
      const data = res.data?.data || {};
      setComments(Array.isArray(data.comments) ? data.comments : []);
      setTotal(Number(data.total) || 0);
    } catch (err) {
      const message = err.response?.data?.message || "Failed to load comments";
      setError(message);
      setComments([]);
      setTotal(0);
      toast.error(message, {
        position: "bottom-right",
        autoClose: 3000,
        hideProgressBar: true,
        theme: "light",
      });
    } finally {
      setLoading(false);
    }
  }, [videoId, page, rowsPerPage]);

  useEffect(() => {
    fetchComments();
  }, [fetchComments]);

  const handleSaveComment = async (content) => {
    if (!editTarget) return;
    setSaving(true);
    try {
      await updateComment(editTarget.id, content);
      toast.success("Comment updated", {
        position: "bottom-right",
        autoClose: 2000,
        theme: "light",
      });
      setEditTarget(null);
      fetchComments();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to update comment", {
        position: "bottom-right",
        autoClose: 3000,
        hideProgressBar: true,
        theme: "light",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteComment = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteComment(deleteTarget.id);
      toast.success("Comment deleted", {
        position: "bottom-right",
        autoClose: 2000,
        theme: "light",
      });
      setDeleteTarget(null);
      fetchComments();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to delete comment", {
        position: "bottom-right",
        autoClose: 3000,
        hideProgressBar: true,
        theme: "light",
      });
    } finally {
      setDeleting(false);
    }
  };

  const emptyMessage = error
    ? error
    : "No comments found for this video.";

  const headerTitle = loadingVideo
    ? "Loading video…"
    : videoTitle || `Video #${videoId}`;

  const metaBits = [videoMeta.category, videoMeta.language].filter(Boolean);

  return (
    <>
      <AdminPageLayout
        eyebrow="Content Library"
        title="Video Comments"
        description={
          metaBits.length
            ? `${headerTitle} · ${metaBits.join(" · ")}`
            : headerTitle
        }
        actions={
          <div
            className="admin-toolbar"
            style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}
          >
            <AdminAction
              variant="outline"
              startIcon={<ArrowBack />}
              onClick={() =>
                navigate(location.state?.from || "/dashboards/engagement")
              }
            >
              Back
            </AdminAction>
          </div>
        }
      >
        <AdminSectionCard
          title={headerTitle}
          description="View, edit, or delete comments submitted on this video."
          className="admin-table-shell"
        >
          <TableContainer component="div">
            <Table sx={{ minWidth: 700 }} aria-label="video comments table">
              <TableHead>
                <StyledTableRow>
                  <StyledTableCell>Sr. No.</StyledTableCell>
                  <StyledTableCell>User</StyledTableCell>
                  <StyledTableCell>Comment</StyledTableCell>
                  <StyledTableCell>Type</StyledTableCell>
                  <StyledTableCell>Date / Time</StyledTableCell>
                  <StyledTableCell align="right">Actions</StyledTableCell>
                </StyledTableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <StyledTableRow>
                    <StyledTableCell colSpan={6} align="center">
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 1.5,
                          py: 4,
                          color: "#78716c",
                        }}
                      >
                        <CircularProgress size={22} sx={{ color: "#3f7a2a" }} />
                        <span>Loading comments…</span>
                      </Box>
                    </StyledTableCell>
                  </StyledTableRow>
                ) : displayRows.length !== 0 ? (
                  displayRows.map((row, index) => (
                    <StyledTableRow key={`${row.id}-${row.isReply ? "reply" : "root"}`}>
                      <StyledTableCell component="th">
                        {startIndex + index + 1}
                      </StyledTableCell>
                      <StyledTableCell>
                        <div className="font-semibold text-[15px] text-[#172112]">
                          {getUserName(row)}
                        </div>
                      </StyledTableCell>
                      <StyledTableCell>
                        <span
                          title={row.content || ""}
                          style={{
                            display: "block",
                            maxWidth: 420,
                            paddingLeft: row.isReply ? 16 : 0,
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                          }}
                        >
                          {row.content || "—"}
                        </span>
                      </StyledTableCell>
                      <StyledTableCell>
                        <Chip
                          size="small"
                          label={row.isReply ? "Reply" : "Comment"}
                          variant="outlined"
                          sx={{
                            borderColor: row.isReply ? "#bfdbfe" : "#bbf7d0",
                            color: row.isReply ? "#1d4ed8" : "#166534",
                            background: row.isReply ? "#eff6ff" : "#f0fdf4",
                          }}
                        />
                      </StyledTableCell>
                      <StyledTableCell>{formatDateTime(row.createdAt)}</StyledTableCell>
                      <StyledTableCell align="right">
                        <TableRowActions
                          rowId={row.id}
                          onEdit={() => setEditTarget(row)}
                          onDelete={() => setDeleteTarget(row)}
                        />
                      </StyledTableCell>
                    </StyledTableRow>
                  ))
                ) : (
                  <StyledTableRow>
                    <StyledTableCell colSpan={6} align="center">
                      <Box sx={{ py: 4, color: "#78716c" }}>{emptyMessage}</Box>
                    </StyledTableCell>
                  </StyledTableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50]}
            component="div"
            count={total}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(_e, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            sx={{
              "& .MuiTablePagination-toolbar": { borderTop: "1px solid #e7e5e4" },
            }}
          />
        </AdminSectionCard>
      </AdminPageLayout>

      <EditCommentDialog
        open={!!editTarget}
        comment={editTarget}
        onClose={() => setEditTarget(null)}
        onSave={handleSaveComment}
        loading={saving}
      />

      <DeleteConfirmationDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDeleteComment}
        title="Delete Comment"
        itemName={
          deleteTarget
            ? `comment by ${getUserName(deleteTarget)}`
            : undefined
        }
        loading={deleting}
      />
    </>
  );
}
